import { useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { PnlHero } from "@/components/PnlHero";
import { PositionCard } from "@/components/PositionCard";
import { ScannerBoard } from "@/components/ScannerBoard";
import { KillSwitch } from "@/components/KillSwitch";
import { KiteConnect, KiteExpiredBanner } from "@/components/KiteConnect";
import { MarketClock } from "@/components/MarketClock";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/api";
import { toast } from "sonner";
import { Activity, Layers } from "lucide-react";

const levelColor = { TRADE: "#00E676", KILL: "#FF2400", WARN: "#FFB300", INFO: "#8A919E" };

const SWING_STATE = {
  BUY_SUBMITTED: { label: "BUY PENDING", color: "#FFB300" },
  HOLDING: { label: "HOLDING", color: "#007AFF" },
  TARGET_RESTING: { label: "TARGET SET", color: "#00E676" },
  EXIT_SUBMITTED: { label: "SELLING…", color: "#FF3B30" },
};

function SwingBoard({ swing }) {
  if (!swing) return null;
  const positions = swing.positions || [];
  const deployedPct = swing.capital_ceiling ? Math.min(100, (swing.deployed_capital / swing.capital_ceiling) * 100) : 0;
  return (
    <div className="px-4 mb-4" data-testid="swing-board">
      <div className="flex items-center gap-2 mb-2">
        <span className="h-2 w-2 rounded-full bg-pos pulse-dot" />
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-pos">Swing Bot — CNC Auto</h2>
      </div>
      <div className="bg-surface border border-pos/30 rounded-sm p-3 mb-2">
        <div className="grid grid-cols-3 gap-2 mb-2">
          <Stat label="Deployed" value={fmtINR(swing.deployed_capital)} testid="swing-deployed" />
          <Stat label="Ceiling" value={fmtINR(swing.capital_ceiling)} />
          <Stat label="Open" value={`${positions.length}/${swing.max_positions ?? "—"}`} testid="swing-open-slots" />
        </div>
        <div className="h-1.5 w-full bg-elevated rounded-full overflow-hidden">
          <div className="h-full bg-pos transition-all duration-500" style={{ width: `${deployedPct}%` }} />
        </div>
        <div className="mt-1.5 text-[10px] text-muted2">
          Buy +{swing.entry_pct}% · Target +{swing.target_pct}% · ₹{swing.notional_per_order}/order · no stop
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {positions.map((p) => {
          const st = SWING_STATE[p.state] || { label: p.state, color: "#8A919E" };
          const pos = p.pnl >= 0;
          return (
            <div key={p.symbol} className="bg-surface border border-edge rounded-sm px-3 py-2 flex items-center justify-between" data-testid={`swing-pos-${p.symbol}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-white">{p.symbol}</span>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm" style={{ color: st.color, background: st.color + "22" }}>{st.label}</span>
                </div>
                <div className="mono text-[10px] text-muted2 mt-0.5">
                  {p.qty} @ ₹{p.entry_price} → tgt ₹{p.target_price ?? "—"}
                </div>
              </div>
              <div className="text-right">
                <div className={`mono text-sm font-bold ${pos ? "text-pos" : "text-neg"}`}>{fmtINR(p.pnl)}</div>
                <div className={`mono text-[10px] ${pos ? "text-pos" : "text-neg"}`}>{p.pnl_pct >= 0 ? "+" : ""}{p.pnl_pct}%</div>
              </div>
            </div>
          );
        })}
        {positions.length === 0 && (
          <div className="border border-dashed border-edge rounded-sm py-6 text-center text-xs text-muted2" data-testid="no-swing-positions">
            No swing holdings yet. Bot buys when a stock crosses +{swing.entry_pct}% intraday.
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-elevated border border-edge rounded-sm px-2 py-1.5" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-muted2">{label}</div>
      <div className="mono text-xs font-bold text-white mt-0.5">{value}</div>
    </div>
  );
}

function ActivityFeed() {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    const load = () => api.activity().then((d) => setLogs(d.logs || [])).catch(() => {});
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="px-4" data-testid="activity-feed">
      <div className="flex items-center gap-2 mb-2">
        <Activity size={14} className="text-brand" />
        <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-muted2">Activity Log</h2>
      </div>
      <div className="border border-edge rounded-sm bg-surface max-h-[220px] overflow-y-auto no-scrollbar divide-y divide-edge/50">
        {logs.map((l, i) => (
          <div key={i} className="flex items-start gap-2 px-3 py-1.5">
            <span className="mono text-[10px] text-muted2 mt-0.5 shrink-0">
              {(l.ts || "").slice(11, 19)}
            </span>
            <span className="mono text-[11px] leading-snug" style={{ color: levelColor[l.level] || "#fff" }}>
              {l.message}
            </span>
          </div>
        ))}
        {logs.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted2">No activity yet.</div>}
      </div>
    </div>
  );
}

export default function Home({ snapshot, connected }) {
  const positions = snapshot?.open_positions || [];
  const caps = snapshot?.caps || {};

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("kite") === "connected") {
      toast.success("Kite login complete", { description: "Live market data connected" });
      window.history.replaceState({}, "", "/");
    }
  }, []);

  return (
    <div className="pb-40">
      <TopBar snapshot={snapshot} connected={connected} />
      {snapshot?.kite_auth_expired && (
        <div className="px-4 mt-3"><KiteExpiredBanner show /></div>
      )}
      <KiteConnect kite={snapshot?.kite} />
      <MarketClock market={snapshot?.market} />
      <PnlHero snapshot={snapshot} />

      {snapshot?.mode === "LIVE" && (
        <div className="mx-4 mb-3 bg-neg/15 border border-neg/40 rounded-sm px-3 py-2 flex items-center justify-center gap-2" data-testid="live-mode-banner">
          <span className="h-2 w-2 rounded-full bg-neg pulse-dot" />
          <span className="text-neg font-bold text-xs tracking-widest">LIVE MODE — REAL MONEY ORDERS ACTIVE</span>
        </div>
      )}

      {snapshot?.killed && (
        <div className="mx-4 mb-3 bg-kill/15 border border-kill/40 rounded-sm px-3 py-2 text-center" data-testid="killed-banner">
          <span className="text-kill font-bold text-xs tracking-widest">KILL SWITCH ACTIVE — ENTRIES HALTED</span>
        </div>
      )}

      {snapshot?.strategy === "SWING" && <SwingBoard swing={snapshot?.swing} />}

      {/* Positions */}
      <div className="px-4 mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Layers size={14} className="text-brand" />
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-muted2">
              {snapshot?.strategy === "SWING" ? "Scalp Positions" : "Open Positions"}
            </h2>
          </div>
          <span className="mono text-[11px] text-muted2" data-testid="slots-used">
            {caps.slots_used ?? 0}/{caps.max_concurrent ?? 5} slots
          </span>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {positions.map((p) => (
            <PositionCard key={p.symbol} pos={p} />
          ))}
          {positions.length === 0 && (
            <div className="border border-dashed border-edge rounded-sm py-8 text-center text-xs text-muted2" data-testid="no-positions">
              {snapshot?.strategy === "SWING" ? "Swing mode active — no MIS scalp positions." : "No open positions. Scanner is hunting momentum…"}
            </div>
          )}
        </div>
      </div>

      <div className="mb-4">
        <ScannerBoard rows={snapshot?.scanner || []} />
      </div>

      <ActivityFeed />

      <KillSwitch killed={snapshot?.killed} />
    </div>
  );
}
